import {useEffect, useState} from 'react'
import {
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query'

import {
  ApplicationBypassData,
  ApplicationOSType,
  BypassDataClient,
  BypassType,
  DefaultBypassApiItem,
  DefaultBypassState,
  TrafficType,
  UrlBypassData,
} from 'interfaces'
import {PoliciesService} from 'services/api'
import {createEmptyBypassData} from './useGetBypass'
import {getApplicationBypassResourceQueryKey} from './useGetApplicationBypassResource'
import {getUrlBypassResourceQueryKey} from './useGetUrlBypassResource'

export const GET_URL_AND_APPLICATION_BYPASS_QUERY_KEY =
  'getUrlAndApplicationBypassData'

interface GetUrlAndApplicationBypassQueryProps {
  policyName: string
  enabled?: boolean
}

interface BypassResourceData {
  applicationBypass: ApplicationBypassData
  urlBypass: UrlBypassData
}

interface GetUrlAndApplicationBypassQueryResponse
  extends Omit<UseQueryResult<BypassDataClient>, 'data'> {
  data: BypassDataClient
  onUpdateBypassData: (data: BypassDataClient) => void
}

interface BypassDataState {
  data: BypassDataClient
  ownerKey: string
}

const getBypassDataOwnerKey = (policyName: string, enabled: boolean): string =>
  enabled ? policyName : ''

export const useGetUrlAndApplicationBypass = ({
  policyName,
  enabled = true,
}: GetUrlAndApplicationBypassQueryProps): GetUrlAndApplicationBypassQueryResponse => {
  const queryClient = useQueryClient()
  const bypassDataOwnerKey = getBypassDataOwnerKey(policyName, enabled)
  const [bypassDataState, setBypassDataState] = useState<BypassDataState>(
    () => ({
      data: createEmptyBypassData(),
      ownerKey: bypassDataOwnerKey,
    }),
  )

  const {data, isLoading, isSuccess, isError, error, ...rest} = useQuery({
    queryKey: [GET_URL_AND_APPLICATION_BYPASS_QUERY_KEY, policyName],
    queryFn: async (): Promise<BypassResourceData> => {
      const [{data: urlBypass}, {data: applicationBypass}] = await Promise.all([
        queryClient.fetchQuery({
          queryKey: getUrlBypassResourceQueryKey(policyName),
          queryFn: () => PoliciesService.getUrlBypass(policyName),
        }),
        queryClient.fetchQuery({
          queryKey: getApplicationBypassResourceQueryKey(policyName),
          queryFn: () => PoliciesService.getApplicationBypass(policyName),
        }),
      ])

      return {
        applicationBypass,
        urlBypass,
      }
    },
    enabled,
    select: transformBypassResourcesToClientData,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    setBypassDataState(currentState => {
      if (currentState.ownerKey === bypassDataOwnerKey) {
        return currentState
      }

      return {
        data: createEmptyBypassData(),
        ownerKey: bypassDataOwnerKey,
      }
    })
  }, [bypassDataOwnerKey])

  useEffect(() => {
    if (!enabled || isLoading || !data) {
      return
    }

    setBypassDataState({
      data,
      ownerKey: bypassDataOwnerKey,
    })
  }, [enabled, isLoading, data, bypassDataOwnerKey])

  const onUpdateBypassData = (updatedData: BypassDataClient): void =>
    setBypassDataState({
      data: updatedData,
      ownerKey: bypassDataOwnerKey,
    })

  const bypassData =
    bypassDataState.ownerKey === bypassDataOwnerKey
      ? bypassDataState.data
      : createEmptyBypassData()

  return {
    data: bypassData,
    onUpdateBypassData,
    isLoading,
    isSuccess,
    isError,
    error,
    ...rest,
  }
}

const toTrafficType = (state: DefaultBypassState): TrafficType =>
  state === DefaultBypassState.IGNORED ? TrafficType.IGNORE : TrafficType.BYPASS

const getIgnoredDefaultNames = (items: DefaultBypassApiItem[]): string[] =>
  items
    .filter(item => item.state === DefaultBypassState.IGNORED)
    .map(item => item.name)

const sortByUpdatedAtDesc = <T extends {updatedAt?: string}>(items: T[]): T[] =>
  [...items].sort(
    (a, b) =>
      new Date(b.updatedAt || 0).getTime() -
      new Date(a.updatedAt || 0).getTime(),
  )

export const transformUrlBypassToClientData = (
  urlBypass: UrlBypassData,
): BypassDataClient => ({
  customDomainBypassList: sortByUpdatedAtDesc(
    urlBypass.custom.map(item => ({
      ...item,
      isEdit: false,
      isDefault: false,
      bypassType: BypassType.url,
    })),
  ),
  domainDefaultBypassList: urlBypass.default.map(item => ({
    name: item.name,
    bypassType: BypassType.url,
    isDefault: true,
    traffic: toTrafficType(item.state),
    isEdit: false,
  })),
  domainIgnoreBypassList: getIgnoredDefaultNames(urlBypass.default),
  customApplicationBypassList: [],
  applicationDefaultBypassList: [],
  applicationIgnoreBypassList: [],
  inheritsApplicationBypass: false,
  inheritsDomainBypass: urlBypass.inheritsFromBase,
})

const transformBypassResourcesToClientData = ({
  applicationBypass,
  urlBypass,
}: BypassResourceData): BypassDataClient => {
  const ignoredMacApps = getIgnoredDefaultNames(applicationBypass.default.mac)
  const ignoredWindowsApps = getIgnoredDefaultNames(
    applicationBypass.default.windows,
  )
  const urlBypassData = transformUrlBypassToClientData(urlBypass)

  return {
    ...urlBypassData,
    customApplicationBypassList: sortByUpdatedAtDesc([
      ...applicationBypass.custom.mac.map(item => ({
        ...item,
        isEdit: false,
        bypassType: BypassType.app,
        os: ApplicationOSType.MAC,
      })),
      ...applicationBypass.custom.windows.map(item => ({
        ...item,
        isEdit: false,
        bypassType: BypassType.app,
        os: ApplicationOSType.WINDOWS,
      })),
    ]),
    applicationDefaultBypassList: [
      ...applicationBypass.default.mac.map(item => ({
        name: item.name,
        bypassType: BypassType.app,
        isDefault: true,
        os: ApplicationOSType.MAC,
        traffic: toTrafficType(item.state),
        isEdit: false,
      })),
      ...applicationBypass.default.windows.map(item => ({
        name: item.name,
        bypassType: BypassType.app,
        isDefault: true,
        os: ApplicationOSType.WINDOWS,
        traffic: toTrafficType(item.state),
        isEdit: false,
      })),
    ],
    applicationIgnoreBypassList: [
      ...ignoredMacApps.map(item => ({
        name: item,
        bypassType: BypassType.app,
        os: ApplicationOSType.MAC,
        isDefault: false,
        isEdit: false,
      })),
      ...ignoredWindowsApps.map(item => ({
        name: item,
        bypassType: BypassType.app,
        os: ApplicationOSType.WINDOWS,
        isDefault: false,
        isEdit: false,
      })),
    ],
    inheritsApplicationBypass: applicationBypass.inheritsFromBase,
  }
}
